/**
 * Smart Source Display Utility
 * Converts a story's source_articles array into human-readable source names.
 * e.g. "Bloomberg, Financial Times +14 more" instead of "17 sources"
 */

// ─── Source Classification ─────────────────────────────────────────────────────

// Known Indian/regional source keys (normalized: lowercase, alphanumeric only)
const INDIAN_SOURCE_KEYS = new Set([
  'economictimes', 'livemint', 'mint', 'businesstoday', 'businessstandard',
  'financialexpress', 'moneycontrol', 'businessline', 'thehindubusinessline',
  'outlookbusiness', 'thehindu', 'hindustantimes', 'ndtv', 'indiatimes',
  'timesofindia', 'deccanherald', 'tribuneindia', 'vcircle', 'inc42',
  'yourstory', 'entrackr', 'themorningcontext', 'thekenco', 'theken',
  'newsbytes', 'dealstreetasia', 'edgemarkets', 'businessline',
]);

// Source type classification — used in "View All Sources" modal
const SOURCE_TYPE_MAP = {
  'News Wire':      ['reuters', 'bloomberg', 'ap', 'apnews', 'afp'],
  'Newspaper':      ['ft', 'financialtimes', 'wsj', 'wallstreetjournal', 'nyt', 'newyorktimes',
                     'washingtonpost', 'theguardian', 'bbc', 'bbcnews', 'economist', 'theeconomist',
                     'economictimes', 'timesofindia', 'hindustantimes', 'thehindu', 'deccanherald',
                     'tribuneindia', 'straitstimes', 'scmp', 'nikkei', 'nikkeiasia'],
  'Tech Press':     ['techcrunch', 'wired', 'theverge', 'venturebeat', 'arstechnica', 'engadget',
                     'zdnet', 'cloudwars', 'androidpolice', 'androidauthority', 'inc42', 'yourstory',
                     'techinasia', '9to5google', '9to5mac', 'macrumors', 'appleinsider', 'tomsguide'],
  'Business Press': ['forbes', 'fortune', 'cnbc', 'axios', 'businessstandard', 'livemint', 'mint',
                     'moneycontrol', 'financialexpress', 'businesstoday', 'outlookbusiness',
                     'businessinsider', 'seekingalpha', 'marketwatch', 'barrons', 'vcircle',
                     'entrackr', 'channelnewsasia', 'morningstar', 'thestreet', 'benzinga'],
};

// Build reverse lookup map O(1)
const SOURCE_TYPE_LOOKUP = {};
Object.entries(SOURCE_TYPE_MAP).forEach(([type, keys]) => {
  keys.forEach(k => { SOURCE_TYPE_LOOKUP[k] = type; });
});

/**
 * Returns true if the source is Indian/regional.
 * Checks known key list AND .in TLD in article URL.
 */
export function isIndianSource(sourceName, articleUrl) {
  const key = (sourceName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (INDIAN_SOURCE_KEYS.has(key)) return true;
  if (articleUrl && /\.in(\/|$)/.test(articleUrl)) return true;
  return false;
}

/**
 * Returns source type label for modal display.
 * Options: News Wire | Newspaper | Tech Press | Business Press | Press
 */
export function getSourceType(sourceName) {
  const key = (sourceName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return SOURCE_TYPE_LOOKUP[key] || 'Press';
}

// Map: normalized key (lowercase, no separators) → display name
const SOURCE_DISPLAY_NAMES = {
  // Global wire / tier-1
  reuters: "Reuters",
  bloomberg: "Bloomberg",
  ft: "Financial Times",
  financialtimes: "Financial Times",
  wsj: "Wall Street Journal",
  wallstreetjournal: "Wall Street Journal",
  nyt: "New York Times",
  newyorktimes: "New York Times",
  washingtonpost: "Washington Post",
  theguardian: "The Guardian",
  bbc: "BBC",
  bbcnews: "BBC News",
  apnews: "AP News",
  ap: "AP",
  afp: "AFP",
  cnbc: "CNBC",
  cnn: "CNN",
  axios: "Axios",
  economist: "The Economist",
  theeconomist: "The Economist",
  businessinsider: "Business Insider",
  forbes: "Forbes",
  fortune: "Fortune",
  time: "TIME",
  wired: "Wired",
  quartz: "Quartz",
  techcrunch: "TechCrunch",
  theverge: "The Verge",
  venturebeat: "VentureBeat",
  arstechnica: "Ars Technica",
  engadget: "Engadget",
  zdnet: "ZDNet",
  seekingalpha: "Seeking Alpha",
  marketwatch: "MarketWatch",
  investopedia: "Investopedia",
  investing: "Investing.com",
  // India – business / finance
  economictimes: "Economic Times",
  livemint: "Livemint",
  mint: "Mint",
  businesstoday: "Business Today",
  businessstandard: "Business Standard",
  financialexpress: "Financial Express",
  moneycontrol: "Moneycontrol",
  businessline: "BusinessLine",
  thehindubusinessline: "The Hindu BusinessLine",
  outlookbusiness: "Outlook Business",
  thehindu: "The Hindu",
  hindustantimes: "Hindustan Times",
  ndtv: "NDTV",
  indiatimes: "Economic Times",
  timesofindia: "Times of India",
  deccanherald: "Deccan Herald",
  tribuneindia: "Tribune India",
  // India – startup / VC
  vcircle: "VCCircle",
  dealstreetasia: "DealStreet Asia",
  inc42: "Inc42",
  yourstory: "YourStory",
  entrackr: "Entrackr",
  themorningcontext: "The Morning Context",
  thekenco: "The Ken",
  theken: "The Ken",
  // Asia / SEA
  straitstimes: "The Straits Times",
  channelnewsasia: "Channel NewsAsia",
  scmp: "South China Morning Post",
  nikkei: "Nikkei Asia",
  nikkeiasia: "Nikkei Asia",
  techinasia: "Tech in Asia",
  edgemarkets: "The Edge Markets",
  // Tech / cloud
  cloudwars: "Cloud Wars",
  androidpolice: "Android Police",
  androidauthority: "Android Authority",
  "9to5google": "9to5Google",
  "9to5mac": "9to5Mac",
  macrumors: "MacRumors",
  appleinsider: "AppleInsider",
  tomsguide: "Tom's Guide",
  // Finance / data
  morningstar: "Morningstar",
  barrons: "Barron's",
  thestreet: "The Street",
  zacks: "Zacks",
  benzinga: "Benzinga",
  proactiveinvestors: "Proactive Investors",
};

/**
 * Convert a raw source_name slug to a pretty display name.
 * Lookup key = lowercase + strip all non-alphanumeric chars.
 * Fallback = title-case each hyphen/dot/underscore-separated word.
 */
export function prettifySourceName(raw) {
  if (!raw) return "";
  const key = raw.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (SOURCE_DISPLAY_NAMES[key]) return SOURCE_DISPLAY_NAMES[key];
  // Fallback: title-case words split by hyphens / dots / underscores / spaces
  return raw
    .split(/[-_.\/\s]+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Returns a smart source display string for an article array (Company News context).
 * Prioritises Tier 1/2 global sources; excludes Indian/regional sources.
 * Format: "Bloomberg, TechCrunch +12 more" or "24 sources"
 */
const GLOBAL_TIER_KEYS = new Set([
  // Tier 1
  'ft', 'financialtimes', 'wsj', 'wallstreetjournal', 'bloomberg', 'reuters',
  'economist', 'theeconomist', 'forbes', 'fortune',
  // Tier 2
  'techcrunch', 'wired', 'theverge', 'cnbc', 'bbc', 'bbcnews',
  'theguardian', 'nyt', 'newyorktimes', 'washingtonpost', 'axios',
  'apnews', 'ap', 'businessinsider', 'venturebeat',
]);

export function getSmartSources(articles) {
  if (!articles || !articles.length) return null;

  const seen = new Set();
  const namedSources = [];
  let totalCount = 0;

  articles.forEach(a => {
    const name = (a.publisher || a.source_name || '').trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (seen.has(key)) return;
    seen.add(key);
    if (isIndianSource(name, a.url || '')) return; // exclude Indian
    totalCount++;
    if (GLOBAL_TIER_KEYS.has(key) && namedSources.length < 2) {
      namedSources.unshift(prettifySourceName(name)); // tier sources first
    } else if (namedSources.length < 2 && !GLOBAL_TIER_KEYS.has(key)) {
      namedSources.push(prettifySourceName(name));
    }
  });

  if (totalCount === 0) return null;

  // Re-compute to ensure tier sources come first
  const tierNames = [];
  const otherNames = [];
  seen.forEach(key => {
    // rebuild from seen — only way to prioritise tier
  });

  const displayNames = [];
  // Second pass: prioritise tier sources for display
  const articleNames = articles
    .map(a => (a.publisher || a.source_name || '').trim())
    .filter(Boolean);
  const uniqueNames = [...new Set(articleNames)];
  const tierFirst = uniqueNames.sort((a, b) => {
    const ak = a.toLowerCase().replace(/[^a-z0-9]/g, '');
    const bk = b.toLowerCase().replace(/[^a-z0-9]/g, '');
    const aTier = GLOBAL_TIER_KEYS.has(ak) ? 0 : 1;
    const bTier = GLOBAL_TIER_KEYS.has(bk) ? 0 : 1;
    return aTier - bTier;
  });

  let nonIndianCount = 0;
  const display = [];
  tierFirst.forEach(name => {
    if (isIndianSource(name, '')) return;
    nonIndianCount++;
    if (display.length < 2) display.push(prettifySourceName(name));
  });

  if (nonIndianCount === 0) return `${articles.length} articles`;
  const remainder = nonIndianCount - display.length;
  if (remainder > 0) return `${display.join(', ')} +${remainder} more`;
  return display.join(', ');
}

/**
 * Returns a smart source display string for a story.
 * Shows up to 2 pretty source names + overflow count.
 */
export function getSmartSourceDisplay(story) {
  const articles = story.source_articles || story.articles || [];

  // Collect unique, non-empty source names
  const rawNames = [...new Set(
    articles
      .map(a => (a.source_name || "").trim())
      .filter(Boolean)
  )];

  if (!rawNames.length) {
    const count = story.source_count || 0;
    return count > 0 ? `${count} source${count !== 1 ? "s" : ""}` : null;
  }

  const pretty = rawNames.map(prettifySourceName);
  const total = pretty.length;

  if (total <= 2) return pretty.join(", ");

  return `${pretty.slice(0, 2).join(", ")} +${total - 2} more`;
}
